import { createContext, useCallback, useContext, useReducer } from "react";
import { supabase } from "../lib/supabase";
import { useCompany } from "../hooks/useCompany";
import type { StorageOption } from "../utils/storageOptionUtils";
import type { ServiceLevel } from "../utils/serviceLevelUtils";
import { isFclBookingType } from "../utils/serviceLevelUtils";

export interface BookingState {
  id?: number;
  status: string;
  reference?: string;
  rejection_reason?: string | null;
  transport_type?: number;
  company_id: number | null;
  booking_type?: number;
  /** FCL-only: budget or premium */
  service_level?: ServiceLevel | null;
  origin_pickup_date?: string;
  booking_incoterm?: number;
  pickupFee?: number;
  includeExportClearanceLicense?: boolean;
  currentStep: number;
  isComplete: boolean;
  commercial_invoice_file?: string;
  customer_packing_slip_file?: string;
  supplier_id?: number | null;
  delivery_type?: "storage" | "address";
  storage_option?: StorageOption | null;
  delivery_details?: {
    id: number;
    country: string;
    city: string;
    postal_code: string;
    street_name: string;
    house_number: string;
    instructions: string | null;
  };
  products?: Array<{
    id?: number;
    name: string;
    hs_code: string | null;
    gross_weight: number | null;
    cbm: number | null;
    value: number | null;
    isNew?: boolean;
    ean?: number | null;
    pieces?: number | null;
    cartons?: number | null;
  }>;
}

// Function to filter out properties that don't exist in the database schema
// This helps prevent errors when sending data to the database
const filterBookingData = (
  data: Partial<BookingState>
): Partial<BookingState> => {
  // Create a new object with only the database fields
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const {
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    pickupFee,
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    includeExportClearanceLicense,
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    customer_packing_slip_file,
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    delivery_type, // Not a column in bookings table, stored in booking_delivery
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    delivery_details, // Not a column in bookings table, stored in booking_delivery
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    currentStep, // Not a database field, only used for UI state
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    isComplete, // Not a database field, only used for UI state
    ...databaseFields
  } = data;
  return databaseFields;
};

// Enforce business rules for booking data
const enforceBusinessRules = (
  data: Partial<BookingState>
): Partial<BookingState> => {
  const result = { ...data };

  // Rule: If transport_type is not 2 (seafreight), booking_type must be null
  if (data.transport_type !== undefined && data.transport_type !== 2) {
    result.booking_type = null as unknown as undefined;
    result.service_level = null;
  }

  // Rule: service_level only applies to FCL
  const bookingType =
    result.booking_type !== undefined ? result.booking_type : data.booking_type;
  if (bookingType !== undefined && !isFclBookingType(bookingType)) {
    result.service_level = null;
  }

  return result;
};

type BookingAction =
  | {
      type: "UPDATE_STEP";
      payload: { step: number; data: Partial<BookingState> };
    }
  | { type: "SET_BOOKING_ID"; payload: number }
  | { type: "RESET" };

const initialState: BookingState = {
  status: "concept",
  company_id: null,
  currentStep: 0,
  isComplete: false,
};
const TOTAL_STEPS = 6; // Transport, Supplier, Product, Basic, Delivery, Review

const bookingReducer = (
  state: BookingState,
  action: BookingAction
): BookingState => {
  let products;
  let updatedState;

  switch (action.type) {
    case "UPDATE_STEP": {
      const hasDataChanges = Object.keys(action.payload.data).length > 0;
      if (
        !hasDataChanges &&
        action.payload.step === state.currentStep
      ) {
        return state;
      }

      console.log("Reducer - UPDATE_STEP:", action.payload);

      // Make sure products are properly handled
      products = action.payload.data.products || state.products;

      if (products) {
        console.log("Reducer - Products count:", products.length);
      }

      // Log status update
      if (action.payload.data.status) {
        console.log("Reducer - Status update:", action.payload.data.status);
      }

      // Create the updated state
      updatedState = {
        ...state,
        ...action.payload.data,
        products: products, // Explicitly set products
        currentStep: action.payload.step,
        isComplete: action.payload.step === TOTAL_STEPS - 1,
      };

      // Log the final state
      console.log("Reducer - Updated state:", {
        ...updatedState,
        products: updatedState.products
          ? `${updatedState.products.length} products`
          : "no products",
      });

      return updatedState;
    }
    case "SET_BOOKING_ID":
      return {
        ...state,
        id: action.payload,
      };
    case "RESET":
      return initialState;
    default:
      return state;
  }
};

interface BookingWizardContextType {
  state: BookingState;
  dispatch: React.Dispatch<BookingAction>;
  updateStep: (step: number, data: Partial<BookingState>) => Promise<void>;
  resetBooking: () => void;
  setCurrentStep: (step: number) => void;
}

const BookingWizardContext = createContext<
  BookingWizardContextType | undefined
>(undefined);

interface BookingWizardProviderProps {
  children: React.ReactNode;
  initialData?: BookingState;
}

export const BookingWizardProvider = ({
  children,
  initialData,
}: BookingWizardProviderProps) => {
  const { selectedCompany } = useCompany();

  // Create initial state with logging
  const mergedInitialState = {
    ...initialState,
    ...(initialData && {
      ...initialData,
      company_id: selectedCompany,
    }),
  };

  // Make sure we never reset to step 0 if initialData already had a currentStep
  if (initialData?.currentStep) {
    console.log(
      "BookingWizardProvider - Preserving initialData.currentStep:",
      initialData.currentStep
    );
    mergedInitialState.currentStep = initialData.currentStep;
  }

  // Log the initial state for debugging
  console.log("BookingWizardProvider - Initial state:", {
    ...mergedInitialState,
    currentStep: mergedInitialState.currentStep,
    products: mergedInitialState.products
      ? `${mergedInitialState.products.length} products`
      : "no products",
  });

  const [state, dispatch] = useReducer(bookingReducer, mergedInitialState);

  const setCurrentStep = useCallback((step: number) => {
    dispatch({
      type: "UPDATE_STEP",
      payload: {
        step,
        data: {},
      },
    });
  }, []);

  const updateStep = async (step: number, data: Partial<BookingState>) => {
    let databaseError: Error | null = null;

    try {
      // Log the requested step change
      console.log("BookingWizardContext - updateStep called with:", {
        step,
        data,
      });

      // Extract products from data to handle separately
      const { products, ...bookingData } = data;

      // Apply business rules
      const bookingDataWithRules = enforceBusinessRules(bookingData);

      // Filter out properties that don't exist in the database schema
      const filteredBookingData = filterBookingData(bookingDataWithRules);

      // Prepare booking data without products
      const bookingUpdateData = {
        ...filteredBookingData,
        company_id: selectedCompany,
        transport_type: data.transport_type || state.transport_type,
        booking_type:
          bookingDataWithRules.booking_type !== undefined
            ? bookingDataWithRules.booking_type
            : state.booking_type,
        service_level:
          bookingDataWithRules.service_level !== undefined
            ? bookingDataWithRules.service_level
            : state.service_level,
        booking_incoterm: data.booking_incoterm || state.booking_incoterm,
        origin_pickup_date: data.origin_pickup_date || state.origin_pickup_date,
        supplier_id: data.supplier_id || state.supplier_id,
        status: data.status || state.status,
      };

      // Create or update booking in database
      // If we're moving to the product step (step 2) and don't have a booking ID yet, create the booking
      if (step === 2 && !state.id) {
        // Create new booking
        const { data: newBooking, error } = await supabase
          .from("bookings")
          .insert({
            ...bookingUpdateData,
            company_id: selectedCompany,
            transport_type: data.transport_type || state.transport_type,
            booking_type:
              bookingDataWithRules.booking_type !== undefined
                ? bookingDataWithRules.booking_type
                : data.transport_type !== 2
                ? null
                : state.booking_type,
            service_level:
              bookingDataWithRules.service_level !== undefined
                ? bookingDataWithRules.service_level
                : state.service_level ?? null,
            booking_incoterm: data.booking_incoterm || state.booking_incoterm,
            origin_pickup_date:
              data.origin_pickup_date || state.origin_pickup_date,
            supplier_id: data.supplier_id || state.supplier_id,
            status: "concept",
          })
          .select()
          .single();

        if (error) {
          console.error("Error creating booking:", error);
          throw error;
        }

        if (newBooking) {
          dispatch({ type: "SET_BOOKING_ID", payload: newBooking.id });
          // Update the state.id for the rest of this function
          bookingUpdateData.id = newBooking.id;
        }
      } else if (state.id) {
        // Update existing booking
        const { error } = await supabase
          .from("bookings")
          .update(bookingUpdateData)
          .eq("id", state.id);

        if (error) {
          console.error("Error updating booking:", error);
          databaseError = error;
        }
      }

      // Update state with all data, including products and supplier information
      dispatch({
        type: "UPDATE_STEP",
        payload: {
          step,
          data: {
            ...bookingUpdateData,
            // Add back the non-database fields for the local state
            pickupFee:
              data.pickupFee !== undefined ? data.pickupFee : state.pickupFee,
            includeExportClearanceLicense:
              data.includeExportClearanceLicense !== undefined
                ? data.includeExportClearanceLicense
                : state.includeExportClearanceLicense,
            products: products || state.products,
            supplier_id: data.supplier_id || state.supplier_id,
            // Preserve delivery_type and delivery_details in state (not in DB)
            delivery_type:
              data.delivery_type !== undefined
                ? data.delivery_type
                : state.delivery_type,
            delivery_details:
              data.delivery_details !== undefined
                ? data.delivery_details
                : state.delivery_details,
          },
        },
      });

      // Add debugging after state update
      console.log(
        "BookingWizardContext - updateStep - State updated, step:",
        step
      );

      // Now if there was a database error that we want to propagate, throw it
      if (databaseError) {
        throw databaseError;
      }
    } catch (error) {
      console.error("Error in updateStep:", error);
      throw error;
    }
  };

  const resetBooking = () => dispatch({ type: "RESET" });

  return (
    <BookingWizardContext.Provider
      value={{ state, dispatch, updateStep, resetBooking, setCurrentStep }}
    >
      {children}
    </BookingWizardContext.Provider>
  );
};

export const useBookingWizard = () => {
  const context = useContext(BookingWizardContext);
  if (context === undefined) {
    throw new Error(
      "useBookingWizard must be used within a BookingWizardProvider"
    );
  }
  return context;
};
